import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Palette, Check, X, Loader2, ArrowLeft } from "lucide-react";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useEmailAvailability } from "@/hooks/useEmailAvailability";
import { validatePasswordStrength } from "@/lib/passwordValidation";

// Schema validation messages are handled via i18n in the component
const emailSchema = z.string().email();
const passwordSchema = z.string().min(6);

export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [userType, setUserType] = useState("artist");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Bot trap
  const [signupAttempts, setSignupAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState(0);
  
  const { isChecking: checkingEmail, isAvailable: emailAvailable } = useEmailAvailability(signupEmail);

  // Redirect if already logged in
  useEffect(() => {
    if (ready && user) {
      navigate('/home', { replace: true });
    }
  }, [ready, user, navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if filled, it's a bot
    if (honeypot) {
      // Silently reject but appear successful to confuse bots
      toast.success(t('authErrors.honeypotSuccess'));
      return;
    }

    // UI throttling - prevent rapid signup attempts
    const now = Date.now();
    if (signupAttempts >= 3 && now - lastAttemptTime < 60000) {
      toast.error(t('authErrors.tooManyAttempts'));
      return;
    }
    
    // Reset attempts after 1 minute
    if (now - lastAttemptTime >= 60000) {
      setSignupAttempts(0);
    }
    
    setSignupAttempts(prev => prev + 1);
    setLastAttemptTime(now);

    if (!acceptedTerms) {
      toast.error(t('authErrors.mustAcceptTerms'));
      return;
    }
    
    // Check password confirmation
    if (signupPassword !== confirmPassword) {
      toast.error(t('authErrors.passwordsDontMatch'));
      return;
    }
    
    // Validate username
    if (!username || username.trim().length === 0) {
      toast.error(t('authErrors.usernameRequired'));
      return;
    }
    
    if (username.length < 3) {
      toast.error(t('authErrors.usernameMinLength'));
      return;
    }

    // Check email availability
    if (emailAvailable === false) {
      toast.error(t('authErrors.emailAlreadyRegistered'));
      return;
    }

    // Check password strength
    const passwordStrength = validatePasswordStrength(signupPassword);
    if (passwordStrength.score < 3) {
      toast.error(t('authErrors.passwordTooWeak'), {
        description: t('authErrors.passwordTooWeakDescription'),
      });
      return;
    }
    
    try {
      emailSchema.parse(signupEmail);
      passwordSchema.parse(signupPassword);
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Use i18n for validation errors
        const errorType = error.errors[0].path[0];
        if (error.errors[0].code === 'invalid_string' && error.errors[0].validation === 'email') {
          toast.error(t('authErrors.invalidEmail'));
        } else if (error.errors[0].code === 'too_small') {
          toast.error(t('authErrors.passwordMinLength'));
        } else {
          toast.error(error.errors[0].message);
        }
        return;
      }
    }

    setLoading(true);

    // Check if username already exists
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username.trim())
      .maybeSingle();

    if (existingUser) {
      toast.error(t('authErrors.usernameTaken'));
      setLoading(false);
      return;
    }

    const { data: authData, error } = await supabase.auth.signUp({
      email: signupEmail,
      password: signupPassword,
      options: {
        data: {
          username: username.trim(),
          user_type: userType,
          seller_type: userType === 'artist' ? 'private' : null,
        },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });

    if (error) {
      // Check for specific error messages
      if (error.message.includes('already registered')) {
        toast.error(t('authErrors.emailAlreadyRegisteredLogin'));
      } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
        toast.error(t('authErrors.usernameOrEmailTaken'));
      } else {
        toast.error(error.message);
      }
    } else {
      // If user opted in for newsletter, create email_preferences entry
      if (newsletterConsent && authData?.user?.id) {
        await supabase
          .from('email_preferences')
          .upsert({
            user_id: authData.user.id,
            email: signupEmail.trim().toLowerCase(),
            newsletter_consent: true,
            user_type: userType,
          }, { onConflict: 'email' });
      }

      // Notify admins about new artist signup
      if (userType === 'artist' && authData?.user?.id) {
        supabase.functions.invoke('notify-admins-new-artist', {
          body: {
            artistId: authData.user.id,
            artistName: username.trim(),
            artistEmail: signupEmail.trim().toLowerCase(),
            userType,
          },
        }).catch((err) => console.error('Failed to notify admins:', err));
      }

      if (userType === 'artist') {
        toast.success(t('authErrors.artistAccountCreated'), {
          duration: 6000
        });
      } else {
        toast.success(t('authErrors.buyerAccountCreated'));
      }
      // Redirect to onboarding after registration
      navigate("/onboarding");
    }
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(t('auth.welcomeBack'));
      navigate("/home");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Palette className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('auth.welcome')}</CardTitle>
          <CardDescription>{t('auth.welcomeDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">{t('auth.email')}</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="din@email.se"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">{t('auth.password')}</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="text-right">
                  <Link 
                    to="/forgot-password"
                    className="text-sm text-primary hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
                <div className="text-center pt-2">
                  <Link 
                    to="/" 
                    className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('common.backToStart')}
                  </Link>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                {/* 1. E-post */}
                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="din@email.se"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      className={emailAvailable === false ? "border-destructive" : ""}
                    />
                    {signupEmail && signupEmail.includes('@') && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : emailAvailable === true ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : emailAvailable === false ? (
                          <X className="h-4 w-4 text-destructive" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  {emailAvailable === true && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      ✓ {t('auth.emailAvailable')}
                    </p>
                  )}
                  {emailAvailable === false && (
                    <p className="text-xs text-destructive">
                      ✗ {t('auth.emailTaken')}
                    </p>
                  )}
                </div>

                {/* 2. Lösenord + Bekräfta */}
                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth.password')}</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                  />
                  <PasswordStrengthIndicator password={signupPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={confirmPassword && confirmPassword !== signupPassword ? "border-destructive" : ""}
                  />
                  {confirmPassword && confirmPassword !== signupPassword && (
                    <p className="text-xs text-destructive">{t('auth.passwordsDontMatch')}</p>
                  )}
                  {confirmPassword && confirmPassword === signupPassword && signupPassword.length > 0 && (
                    <p className="text-xs text-green-600 dark:text-green-400">✓ {t('auth.passwordsMatch')}</p>
                  )}
                </div>

                {/* 3. Användarnamn */}
                <div className="space-y-2">
                  <Label htmlFor="signup-username">{t('auth.username')}</Label>
                  <Input
                    id="signup-username"
                    type="text"
                    placeholder={t('authErrors.chooseUniqueUsername')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('auth.usernameHint')}
                  </p>
                </div>

                {/* 5. Kontotyp */}
                <div className="space-y-2">
                  <Label htmlFor="signup-usertype">{t('auth.iAm')}</Label>
                  <Select value={userType} onValueChange={setUserType}>
                    <SelectTrigger id="signup-usertype">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="artist">{t('auth.artistCanBuy')}</SelectItem>
                      <SelectItem value="buyer">{t('auth.buyer')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('auth.asArtist')}
                  </p>
                  
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="absolute -left-[9999px] opacity-0 h-0 w-0"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>


                {/* 7. Villkor (sammanslagen checkbox) */}
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                  />
                  <label htmlFor="accept-terms" className="text-sm text-muted-foreground cursor-pointer">
                    {t('auth.acceptTermsAndPrivacy')}{" "}
                    <a href="/terms" target="_blank" className="text-primary hover:underline">
                      {t('authErrors.termsAndConditions')}
                    </a>{" "}
                    {t('authErrors.and')}{" "}
                    <a href="/privacy" target="_blank" className="text-primary hover:underline">
                      {t('authErrors.privacyPolicy')}
                    </a>
                  </label>
                </div>

                {/* 8. Newsletter consent */}
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="newsletter-consent"
                    checked={newsletterConsent}
                    onCheckedChange={(checked) => setNewsletterConsent(checked as boolean)}
                  />
                  <label htmlFor="newsletter-consent" className="text-sm text-muted-foreground cursor-pointer">
                    {t('auth.newsletterConsent')}
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !acceptedTerms}
                >
                  {loading ? t('auth.creatingAccount') : t('auth.signUp')}
                </Button>
                <div className="text-center pt-2">
                  <Link 
                    to="/" 
                    className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('common.backToStart')}
                  </Link>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
